/**
 * What the curator is told when a lexicon import does not come back.
 *
 * A dry run and a commit are one HTTP request each, both carrying a file,
 * both routed through the ingress — the shape of request most likely to
 * meet a 502, a 504 or a dropped connection. When that happens `fetch` or
 * `response.json()` rejects, and without this the promise was left
 * unhandled: the busy flag cleared and the curator saw nothing at all.
 *
 * The two phases need different words, because they carry different risk.
 * A failed dry run wrote nothing and can simply be repeated. A failed
 * commit may well have been applied server-side — the transaction commits
 * before the response is written — so the honest message says the outcome
 * is unknown and points at the Termen tab, rather than inviting a retry
 * that could double-apply an import the curator cannot see yet.
 */

/** The `detail` of a FastAPI error body, without asserting its shape. */
function detailOf(payload: unknown): unknown {
  if (typeof payload === "object" && payload !== null && "detail" in payload) {
    return (payload as { detail: unknown }).detail;
  }
  return undefined;
}

/** The parenthesised cause, when the failure carried a usable message. */
function reason(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return ` (${error.message.trim()})`;
  return "";
}

/**
 * The message for a response that arrived but said no: `parse()`'s list of
 * per-row errors, a plain `detail` string, or — when the body was not
 * readable at all (an HTML error page from a proxy) — the bare status.
 */
export function importErrorMessage(payload: unknown, status: number): string {
  const detail = detailOf(payload);
  if (typeof detail === "string" && detail.trim()) return detail;
  if (typeof detail === "object" && detail !== null && "errors" in detail) {
    const errors = (detail as { errors: unknown }).errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.map((entry) => String(entry)).join("\n");
    }
  }
  return `De server antwoordde met status ${status} en een onleesbaar antwoord.`;
}

/** A dry run that never came back. Nothing was written, so: retry. */
export function dryRunTransportError(error: unknown): string {
  return (
    "De proefdraai is niet voltooid: de server was onbereikbaar of gaf een " +
    "onleesbaar antwoord. Er is niets gewijzigd — probeer het opnieuw." +
    reason(error)
  );
}

/**
 * A commit whose answer never arrived. The import may or may not have been
 * applied; only the Termen tab can settle that.
 */
export const COMMIT_OUTCOME_UNKNOWN =
  "De import is verstuurd, maar de server gaf geen bevestiging terug. " +
  "Het is onbekend of de import is doorgevoerd. Controleer eerst het tabblad " +
  "Termen voordat u het opnieuw probeert.";

export function commitTransportError(error: unknown): string {
  return COMMIT_OUTCOME_UNKNOWN + reason(error);
}

/**
 * True when a 200 from the commit endpoint did not carry the fields the UI
 * needs. Treated exactly like a transport failure: the write may have
 * happened, and the UI cannot claim it did.
 */
export function isUsableCommitPayload(
  payload: unknown,
): payload is { counts: Record<string, number>; epoch: number } {
  if (typeof payload !== "object" || payload === null) return false;
  if (!("counts" in payload) || !("epoch" in payload)) return false;
  const { counts, epoch } = payload as { counts: unknown; epoch: unknown };
  return typeof counts === "object" && counts !== null && typeof epoch === "number";
}
